import { FGTile, FGStack, FGTypography } from '@fg/design-system';
import { CountryBadge } from 'components/eventsFeed/CountryBadge';
import RiskBadge from 'components/eventsFeed/RiskBadge';
import { IActiveCountry, IBasicActivityRecord } from 'interfaces';
import { map, filter } from 'lodash/fp';
import React from 'react';
import cx from 'classnames';
import InsightMenu, { InsightMenuProps } from './InsightMenu';

export type InsightWithDateString = IBasicActivityRecord & {
    dateString: string;
};

const InsightItem = React.memo(
    ({
        insight,
        countriesById,
        onClick,
        isActive,
        canShare,
        canDownloadData,
        onEdit,
        onBroadcast,
        onDownload,
        onDelete,
        isGQInternalUser,
    }: {
        insight: InsightWithDateString;
        countriesById: { [countryID: number]: IActiveCountry };
        onClick?: (id: string) => void;
        isActive?: boolean;
    } & Omit<InsightMenuProps, 'insight'>) => {
        return (
            <FGTile
                data-testid="insight-item"
                className={cx('event-item-container', {
                    'active-event': isActive,
                })}
                alignItems="flex-start"
                onClick={() => onClick?.(insight._id)}>
                <FGStack direction="column" gap={8} style={{ width: '100%' }}>
                    <FGStack
                        gap={8}
                        style={{ flexWrap: 'nowrap', width: '100%' }}
                        justifyContent="space-between"
                        alignItems="flex-start">
                        <FGStack gap={8} style={{ flexWrap: 'wrap' }}>
                            {map(
                                (countryId: number) => {
                                    const country = countriesById[countryId];
                                    if (!country) return null;
                                    return (
                                        <CountryBadge
                                            country={country}
                                            key={countryId}
                                        />
                                    );
                                },
                                filter(
                                    countryId => !!countriesById[countryId],
                                    insight.countries
                                )
                            )}
                        </FGStack>
                        {isGQInternalUser && (
                            <div onClick={e => e.stopPropagation()}>
                                <InsightMenu
                                    insight={insight}
                                    canShare={canShare}
                                    canDownloadData={canDownloadData}
                                    onEdit={onEdit}
                                    onBroadcast={onBroadcast}
                                    onDownload={onDownload}
                                    onDelete={onDelete}
                                />
                            </div>
                        )}
                    </FGStack>
                    <FGTypography
                        alignment="left"
                        variant="body3-long"
                        color="strong"
                        tag="label">
                        {insight.name}
                    </FGTypography>
                    <FGStack gap={8}>
                        <FGTypography variant="body4" tag="label" color="weak">
                            {insight.dateString}
                        </FGTypography>
                    </FGStack>
                    <FGStack gap={8} style={{ flexWrap: 'wrap' }}>
                        {map(
                            (risk, index) => (
                                <RiskBadge key={index} risk={risk} />
                            ),
                            insight.risks
                        )}
                    </FGStack>
                </FGStack>
            </FGTile>
        );
    }
);

export default InsightItem;
